import React, { useCallback, useEffect, useState } from 'react';
import i18n from 'i18next';
import { Transition } from 'react-transition-group';
import { FormControlLabel, Switch } from '@material-ui/core';
import PropTypes from 'prop-types';
import { Button } from '../../../../common/components/buttons';
import useStyles, { closedWidth, openedWidth } from './PatrolDetails.styles';
import { PatrolExact } from '../../../models/Patrol';
import { formatDate, formatSecondsDuration } from '../../../../common/formatters/timeFormatters';
import PatrolMap from '../../map/PatrolMap';
import { getEntitiesWithVisibility } from '../../../../map/utils/mapFunctions';
import DrawerToggle from '../../../../common/components/navigation/drawer/DrawerToggle';
import RouteTreeView from './treeViews/RouteTreeView';
import RightOfWayTreeView from './treeViews/RightOfWayTreeView';
import CoverageTreeView from './treeViews/CoverageTreeView';
import WorkzoneTreeView from './treeViews/WorkzoneTreeView';
import UrbanAreaTreeView from './treeViews/UrbanAreaTreeView';
import ObstacleTreeView from './treeViews/ObstacleTreeView';
import FlightPathTreeView from './treeViews/FlightPathTreeView';
import AirportTreeView from './treeViews/AirportTreeView';
import PipelineCenterlineTreeView from './treeViews/PipelineCenterlineTreeView';
import { FindingShape } from '../../../../finding/models/Finding';
import HyspexCoverageTreeView from './treeViews/HyspexCoverageTreeView';
import useAgent from '../../../../common/hooks/useAgent';
import patrolAgent from '../../../agents/patrolAgent';
import usePaginatedAgent from '../../../../common/hooks/usePaginatedAgent';

const PatrolDetails = ({ patrol = [], findings }) => {
  const classes = useStyles();

  const [fetchUncoveredAreas, patrolUncoveredAreas] = useAgent(patrolAgent.getUncoveredAreas);
  const [fetchHyperspectralCoverage, patrolHyperspectralCoverage] = useAgent(patrolAgent.getHyperspectralCoverage, true);
  const [fetchCoverage, patrolCoverage] = useAgent(patrolAgent.getCoverage);

  const [flightPath, setFlightPath] = useState(patrol && patrol.flightPath);
  const [coverage, setCoverage] = useState();
  const [hyspexCoverage, setHyspexCoverage] = useState();
  const [routes, setRoutes] = useState(patrol && patrol.routes);
  const [rightOfWays, setRightOfWays] = useState(patrol && patrol.rightOfWays);
  const [workzones, setWorkzones] = useState(patrol && patrol.workzones);
  const [urbanAreas, setUrbanAreas] = useState(patrol && patrol.urbanAreas);
  const [airports, setAirports] = useState(patrol && patrol.airports);
  const [obstacles, setObstacles] = useState(patrol && patrol.obstacles ? getEntitiesWithVisibility(patrol.obstacles, 'root', true) : undefined);
  const [pipelineCenterlines, setPipelineCenterlines] = useState(patrol && patrol.centerlines);
  const [uncoveredAreas, setUncoveredAreas] = useState([]);
  const [centeredGeometries, setCenteredGeometries] = useState();
  const [isDrawerOpened, setIsDrawerOpened] = useState(true);

  const handleDrawerToggle = useCallback(() => setIsDrawerOpened(!isDrawerOpened), [isDrawerOpened]);

  const [resyncPilot] = usePaginatedAgent(patrolAgent.resyncPilot);
  const handleHighlightUncoveredAreasToggle = useCallback(() => {
    const localUncoveredAreas = patrolUncoveredAreas || [];
    setUncoveredAreas((oldUncoveredAreas) => {
      if (oldUncoveredAreas.length > 0) return [];
      setCenteredGeometries([...localUncoveredAreas]);
      return getEntitiesWithVisibility(localUncoveredAreas, 'root', false);
    });
  }, [patrolUncoveredAreas]);

  const handleResyncPatrol = useCallback(() => resyncPilot(patrol.id), [patrol, resyncPilot]);

  useEffect(() => {
    if (patrol.id) {
      fetchUncoveredAreas(patrol.id, patrol.isMissionShared);
      fetchHyperspectralCoverage(patrol.id, patrol.isMissionShared);
      fetchCoverage(patrol.id, patrol.isMissionShared);
    }
  }, [patrol, fetchUncoveredAreas, fetchHyperspectralCoverage, fetchCoverage]);

  useEffect(() => {
    if (patrolHyperspectralCoverage) setHyspexCoverage(patrolHyperspectralCoverage);
  }, [patrolHyperspectralCoverage]);

  useEffect(() => {
    if (patrolCoverage) setCoverage(patrolCoverage);
  }, [patrolCoverage]);

  return (
    <>
      <div className={classes.title}>{i18n.t('mission.missionDetails')}</div>

      <div id="missionDetails" className={classes.details}>
        <Button onClick={handleResyncPatrol} id="buttonResyncPilot" primary>
          {i18n.t('patrol.button.resyncPilot')}
        </Button>
        <div>
          <div className={classes.label}>{i18n.t('patrol.details.startDate')}</div>
          <div className={classes.text}>{formatDate(patrol.startedAt, patrol.startedAtTimezone)}</div>
        </div>
        <div>
          <div className={classes.label}>{i18n.t('patrol.details.endDate')}</div>
          <div className={classes.text}>{formatDate(patrol.completedAt, patrol.completedAtTimezone)}</div>
        </div>
        <div>
          <div className={classes.label}>{i18n.t('patrol.table.pilotName')}</div>
          <div className={classes.text}>{patrol?.pilot?.name ?? '-'}</div>
        </div>
        <div>
          <div className={classes.label}>{i18n.t('patrol.table.pilotLicenseNumber')}</div>
          <div className={classes.text}>{patrol?.pilot?.licenseNumber ?? '-'}</div>
        </div>
        <div>
          <div className={classes.label}>{i18n.t('patrol.details.transferDate')}</div>
          <div className={classes.text}>{patrol.transferredAt ? formatDate(patrol.transferredAt, patrol.transferredAtTimezone) : '-'}</div>
        </div>
        <div>
          <div className={classes.label}>{i18n.t('patrol.details.flightDuration')}</div>
          <div className={classes.text}>{formatSecondsDuration(patrol.flightDuration)}</div>
        </div>
        <div>
          <div className={classes.label}>{i18n.t('patrol.details.inspectionDuration')}</div>
          <div className={classes.text}>{formatSecondsDuration(patrol.inspectionDuration)}</div>
        </div>
        <div>
          <div className={classes.label}>{i18n.t('patrol.details.coverage')}</div>
          <div className={classes.text}>{`${(patrol.coveragePercentage * 100).toFixed(2)} %`}</div>
        </div>
      </div>

      <div className={classes.containerBottom}>
        <Transition in={isDrawerOpened} timeout={100} className={classes.treeView}>
          {(transitionStatus) => {
            const isTransitionOver = transitionStatus === 'entered';
            return (
              <div className={classes.menu} style={{ minWidth: isDrawerOpened ? openedWidth : closedWidth }}>
                <DrawerToggle opened={isDrawerOpened} onDrawerToggle={handleDrawerToggle} />

                <div className={classes.content} style={!isDrawerOpened || !isTransitionOver ? { display: 'none' } : undefined}>
                  <FormControlLabel
                    labelPlacement="start"
                    control={<Switch checked={uncoveredAreas.length > 0} onChange={handleHighlightUncoveredAreasToggle} />}
                    label={i18n.t('patrol.details.uncoveredAreas')}
                    classes={{
                      root: classes.uncoveredAreaToggle,
                      label: classes.uncoveredAreasLabel,
                    }}
                    disabled={patrolUncoveredAreas && patrolUncoveredAreas.length === 0}
                  />

                  {patrol.flightPath && (
                    <FlightPathTreeView
                      flightPath={patrol && patrol.flightPath}
                      setFlightPath={setFlightPath}
                      setCenteredGeometries={setCenteredGeometries}
                      disabled={uncoveredAreas.length > 0}
                    />
                  )}

                  <CoverageTreeView
                    coverage={coverage}
                    setCoverage={setCoverage}
                    setCenteredGeometries={setCenteredGeometries}
                    disabled={uncoveredAreas.length > 0 || !patrolCoverage}
                  />

                  <HyspexCoverageTreeView
                    hyspexCoverage={hyspexCoverage}
                    setHyspexCoverage={setHyspexCoverage}
                    setCenteredGeometries={setCenteredGeometries}
                    disabled={uncoveredAreas.length > 0 || !hyspexCoverage}
                  />

                  <RouteTreeView
                    routes={patrol && patrol.routes}
                    setRoutes={setRoutes}
                    setCenteredGeometries={setCenteredGeometries}
                    disabled={uncoveredAreas.length > 0}
                  />
                  {patrol.centerlines.length !== 0 && (
                    <PipelineCenterlineTreeView
                      pipelineCenterlines={patrol && patrol.centerlines}
                      setPipelineCenterlines={setPipelineCenterlines}
                      setCenteredGeometries={setCenteredGeometries}
                      disabled={uncoveredAreas.length > 0}
                    />
                  )}
                  <RightOfWayTreeView
                    rightOfWays={patrol && patrol.rightOfWays}
                    setRightOfWays={setRightOfWays}
                    setCenteredGeometries={setCenteredGeometries}
                    disabled={uncoveredAreas.length > 0}
                  />
                  {patrol.workzones.length !== 0 && (
                    <WorkzoneTreeView
                      workzones={patrol && patrol.workzones}
                      setWorkzones={setWorkzones}
                      setCenteredGeometries={setCenteredGeometries}
                      disabled={uncoveredAreas.length > 0}
                    />
                  )}
                  {patrol.urbanAreas.length !== 0 && (
                    <UrbanAreaTreeView
                      urbanAreas={patrol && patrol.urbanAreas}
                      setUrbanAreas={setUrbanAreas}
                      setCenteredGeometries={setCenteredGeometries}
                      disabled={uncoveredAreas.length > 0}
                    />
                  )}
                  {patrol.airports.length !== 0 && (
                    <AirportTreeView
                      airports={patrol && patrol.airports}
                      setAirports={setAirports}
                      setCenteredGeometries={setCenteredGeometries}
                      disabled={uncoveredAreas.length > 0}
                    />
                  )}
                  {patrol.obstacles.length !== 0 && (
                    <ObstacleTreeView
                      obstacles={patrol && patrol.obstacles}
                      setObstacles={setObstacles}
                      setCenteredGeometries={setCenteredGeometries}
                      disabled={uncoveredAreas.length > 0}
                    />
                  )}
                </div>
              </div>
            );
          }}
        </Transition>

        <PatrolMap
          patrolId={patrol.id}
          sharedRow={patrol.isMissionShared}
          routes={routes}
          coverage={coverage}
          uncoveredAreas={uncoveredAreas}
          hyspexCoverage={hyspexCoverage}
          workzones={workzones}
          urbanAreas={urbanAreas}
          flightPath={flightPath}
          findings={findings}
          obstacles={obstacles}
          airports={airports}
          rightOfWays={rightOfWays}
          pipelineCenterlines={pipelineCenterlines}
          centeredGeometries={centeredGeometries}
          isMissionShared={patrol.isMissionShared}
        />
      </div>
    </>
  );
};

PatrolDetails.propTypes = {
  findings: PropTypes.arrayOf(FindingShape).isRequired,
  patrol: PatrolExact.isRequired,
};

PatrolDetails.defaultProps = {};

export default PatrolDetails;
